import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, Button, TextField, Typography, Container, CssBaseline, 
    Grid, Link, Select, MenuItem, FormControl, InputLabel 
} from '@mui/material';
import api from '../api/api';

// Opcional: importa tu logo real (recomendado)
import logo from '../assets/images/logo.png';  // ← ajusta la ruta y nombre de tu archivo


// Función de utilidad simple para decodificar JWT sin librerías externas
const decodeJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

const getDeviceData = () => {
    const ua = navigator.userAgent;
    let os = "Desconocido";
    let model = navigator.platform;
    
    // Detección de OS
    if (ua.indexOf("Win") !== -1) os = "Windows";
    if (ua.indexOf("Mac") !== -1) os = "MacOS";
    if (ua.indexOf("X11") !== -1) os = "UNIX";
    if (ua.indexOf("Linux") !== -1) os = "Linux";
    if (ua.indexOf("Android") !== -1) os = "Android";
    if (ua.indexOf("iPhone") !== -1) os = "iOS";

    return { os, model };
};

const getOrGenerateDeviceToken = () => {
    let token = localStorage.getItem('device_token');
    if (!token) {
        // Usamos crypto para mayor aleatoriedad
        const array = new Uint32Array(4);
        window.crypto.getRandomValues(array);
        const randomHex = Array.from(array, dec => dec.toString(16).padStart(8, '0')).join('');
        token = `dev_${randomHex}_${Date.now()}`;
        localStorage.setItem('device_token', token);
    }
    return token;
};

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [tempToken, setTempToken] = useState(null);
    const [step, setStep] = useState(1);
    const [sucursales, setSucursales] = useState([]);
    const [selectedSucursal, setSelectedSucursal] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState(null);

    const getRedirectPath = (decoded) => {
    if (!decoded?.id_rol) return '/clientes';
    
    const rol = Number(decoded.id_rol);
    
    if (rol === 5) return '/ruta-programada';
    if (rol === 4) return '/camaras';
    
    return '/clientes'; // resto de roles
};
    
    const navigate = useNavigate();

    const handleError = (err) => {
        if (err.response?.data?.detail) {
            const detail = err.response.data.detail;
            if (Array.isArray(detail)) {
                setError(detail[0]?.msg || 'Error al iniciar sesión');
            } else if (typeof detail === 'string') {
                setError(detail);
            } else {
                setError('Error al iniciar sesión');
            }
        } else {
            setError('Error al iniciar sesión');
        }
    };

    const handleLoginStep1 = async (e) => {
        e.preventDefault();
        setError('');

            localStorage.removeItem('token');

        try {

            // 2. Obtenemos el token del dispositivo
            const deviceToken = getOrGenerateDeviceToken();
            const deviceDetails = getDeviceData();


            const response = await api.post('/usuarios/login-auth', {
                email,
                password,
                device_token: deviceToken, // 3. Lo enviamos al backend
                device_os: deviceDetails.os,
                device_model: deviceDetails.model,
                device_type: "Navegador Web"
            });

            const { sucursales, token } = response.data;
            
            if (sucursales && sucursales.length > 0) {
                setSucursales(sucursales);
                setTempToken(token); 
                setSelectedSucursal(sucursales[0].id_sucursal); 
                setStep(2);
            } else {
                setError('No se encontraron sucursales asociadas a tu cuenta.');
            }
            
        } catch (err) {
            handleError(err);
        }
    };


    const handleLoginStep2 = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSucursal) {
        setError('Por favor, selecciona una sucursal.');
        return;
    }
    if (!tempToken) {
        setError('Error de sesión temporal. Vuelve a iniciar sesión.');
        setStep(1);
        return;
    }

    try {
        // === LIMPIAR SESIÓN ANTERIOR ANTES DE INICIAR NUEVA ===
        localStorage.removeItem('token');
        // Opcional: limpiar otros datos de sesión
        // localStorage.removeItem('device_token'); // no limpiar este si quieres mantener el dispositivo

        const finalResponse = await api.post(`/usuarios/login-auth-sucursal`, 
            { sucursal_login: selectedSucursal },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tempToken}`
                }
            }
        );
        
        const { token } = finalResponse.data;
        
        // Guardar nuevo token
        localStorage.setItem('token', token); 
        setTempToken(null); 
        
        const decoded = decodeJwt(token);
        
        // Dentro de handleLoginStep2, reemplaza esta parte:
        if (decoded) {
            const nombreUsuario = decoded.nombre_usuario || 'Usuario'; 
            const nombreSucursal = decoded.nombre_sucursal || 'la sucursal seleccionada';
            
            // === NUEVO CÓDIGO ===
            const welcomeTitle = `¡Bienvenido ${nombreUsuario}! a sucursal ${nombreSucursal}.`;
            const welcomeNote = `Recuerda que tu IP de ubicación y hora de acceso son monitoreados para auditoría. Ten un buen día.`;
            
            // Guardamos ambos mensajes (puedes usar un objeto o dos estados)
            setWelcomeMessage({ title: welcomeTitle, note: welcomeNote });

            const redirectTo = getRedirectPath(decoded);

            console.log('✅ Login exitoso - Redirigiendo a:', redirectTo, 'Rol:', decoded.id_rol);

            setTimeout(() => {
                navigate(redirectTo, { replace: true });
            }, 6000);

        } else {
            navigate('/clientes', { replace: true });
        }

    } catch (err) {
        console.error('Error en login step 2:', err);
        handleError(err);
    }
};

    const LoginForm = (
        <Box 
            component="form" 
            onSubmit={step === 1 ? handleLoginStep1 : handleLoginStep2} 
            noValidate 
            sx={{ 
                mt: 3,
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
                backdropFilter: 'blur(8px)', // efecto vidrio esmerilado moderno
                maxWidth: 420,
                width: '100%',
            }}
        >
            {step === 1 && (
                <>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Correo Electrónico"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </>
            )}

            {step === 2 && (
                <FormControl fullWidth margin="normal" required>
                    <InputLabel id="sucursal-select-label">Selecciona Sucursal</InputLabel>
                    <Select
                        labelId="sucursal-select-label"
                        id="sucursal-select"
                        value={selectedSucursal}
                        label="Selecciona Sucursal"
                        onChange={(e) => setSelectedSucursal(e.target.value)}
                    >
                        {sucursales.map((sucursal) => (
                            <MenuItem key={sucursal.id_sucursal} value={sucursal.id_sucursal}>
                                {sucursal.nombre_sucursal}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
            
            <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 4, mb: 2, py: 1.5, fontSize: '1.1rem' }}
            >
                {step === 1 ? 'Continuar' : 'Iniciar Sesión'}
            </Button>
      {/*       
            <Grid container>
                <Grid item xs>
                    <Link href="#" variant="body2">
                        ¿Olvidaste tu contraseña?
                    </Link>
                </Grid>
                <Grid item>
                    <Link href="/register" variant="body2">
                        {"¿No tienes una cuenta? Regístrate"}
                    </Link>
                </Grid>
            </Grid>      */}
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                //background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #5e60ce 100%)',
                // Alternativas de gradiente que puedes probar:
                 //background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', // dark mode elegante
                background: 'linear-gradient(120deg, #e9e9e9 0%, #fafafa 100%)', // más claro y fresco
            }}
        >
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 1,
                        mt: -10,
                    }}
                >
                    {welcomeMessage ? (
                        <Box sx={{ 
                            p: 5, 
                            bgcolor: 'rgba(255,255,255,0.95)', 
                            borderRadius: 3, 
                            boxShadow: 6,
                            textAlign: 'center',
                            maxWidth: 500
                        }}>
                            <Typography 
                                component="h1" 
                                variant="h4" 
                                color="primary" 
                                fontWeight="bold" 
                                gutterBottom
                            >
                                {welcomeMessage.title}
                            </Typography>
                            
                            <Typography 
                                variant="body1" 
                                color="text.secondary" 
                                sx={{ mb: 3, lineHeight: 1.6 }}
                            >
                                {welcomeMessage.note}
                            </Typography>
                            
                            <Typography variant="body1" color="text.secondary">
                                Redirigiendo...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Logo grande */}
                            <Box sx={{ mb: 5, textAlign: 'center' }}>
                                <img 
                                    src={logo}                     // ← tu logo real
                                    // src="https://via.placeholder.com/320x120/ffffff/667eea?text=Tu+Logo+Villa" // placeholder de prueba
                                    alt="Logo Villa"
                                    style={{ 
                                        maxWidth: '320px',     // ajusta según tu logo (280–380px suele quedar bien)
                                        height: 'auto',
                                        display: 'block',
                                        margin: '0 auto -50px',
                                    }}
                                />
                             {/*   <Typography 
                                    component="h1" 
                                    variant="h4" 
                                    sx={{ 
                                        color: 'black',
                                        fontWeight: 700,
                                        letterSpacing: '-0.1px',
                                        textShadow: '0 2px 10px rgba(151, 151, 151, 0.3)'
                                    }}
                                >
                                    {step === 1 ? 'Bienvenido' : 'Selecciona tu Sucursal'}
                                </Typography>  */}
                            </Box>

                            {LoginForm}
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default Login;